/**
 * Force-Directed Graph Visualization Component
 * 
 * Uses D3.js to render supply chain entities and relationships
 * as an interactive force-directed graph
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6
 */

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

export interface GraphNode extends d3.SimulationNodeDatum {
  id: string;
  name: string;
  dataTypes: string[];
  naicsCodes: string[];
  regulatoryCategories: string[];
  riskScore: number;
  entityJurisdiction?: string;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

export interface GraphEdge extends d3.SimulationLinkDatum<GraphNode> {
  id: string;
  source: string | GraphNode;
  target: string | GraphNode;
  relationshipType: string;
  financialImpact: number;
  description?: string;
  tension: {
    sourceToTarget: number;
    targetToSource: number;
  };
  maxTension: number;
  contributingFrictions?: Array<{
    id: string;
    name: string;
    frictionType: string;
    direction?: string;
    magnitude?: number;
    baselineTension?: number;
    frictionDirection?: string;
  }>;
}

export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

interface ForceDirectedGraphProps {
  data: GraphData;
  width?: number;
  height?: number;
  showDataTypeIndicators?: boolean;
  onNodeClick?: (nodeId: string) => void;
  onEdgeClick?: (edgeId: string) => void;
  onNodeHover?: (nodeId: string | null) => void;
  selectedEdgeId?: string;
}

/**
 * Force-Directed Graph Component
 * 
 * Complete D3.js implementation with:
 * - Force simulation with charge, link, center, and collision forces
 * - Visual encoding for data types, NAICS codes, and regulatory categories
 * - Tension-based edge coloring (green to red gradient)
 * - Financial impact-based edge thickness
 * - Risk score-based node sizing
 * - Conflict hotspot highlighting with glow effect
 * - Pan and zoom with d3.zoom()
 * - Node dragging with d3.drag()
 * - Interactive tooltips and click handlers
 */
export const ForceDirectedGraph: React.FC<ForceDirectedGraphProps> = ({
  data,
  width = 800,
  height = 600,
  showDataTypeIndicators = true,
  onNodeClick,
  onEdgeClick,
  onNodeHover,
  selectedEdgeId,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredEdge, setHoveredEdge] = useState<string | null>(null);
  const [clickedEdge, setClickedEdge] = useState<string | null>(null);

  useEffect(() => {
    if (!svgRef.current || !data.nodes.length) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    // Create deep copies to avoid mutating props
    const nodes: GraphNode[] = data.nodes.map(n => ({ ...n }));
    const edges: GraphEdge[] = data.edges.map(e => ({ ...e }));

    // Initialize D3 force simulation with improved spacing and edge crossing minimization
    const simulation = d3.forceSimulation<GraphNode>(nodes)
      .force('charge', d3.forceManyBody<GraphNode>().strength(-1200)) // Stronger repulsion for more spread
      .force('link', d3.forceLink<GraphNode, GraphEdge>(edges)
        .id(d => d.id)
        .distance(250) // Longer links for more spread
        .strength(0.5)) // Weaker link strength allows more flexibility
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide<GraphNode>().radius(45).strength(1.2)) // Constant radius + padding
      .force('x', d3.forceX(width / 2).strength(0.05)) // Gentle centering
      .force('y', d3.forceY(height / 2).strength(0.05))
      .alphaDecay(0.02); // Slower cooling for better layout convergence

    // Create SVG container
    const svg = d3.select(svgRef.current);
    
    // Create zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform.toString());
      });
    
    svg.call(zoom);

    // Create main group for zoom/pan
    const g = svg.append('g');

    // Create arrow markers for directed edges
    const defs = svg.append('defs');
    
    // Create gradient for tension visualization
    const gradient = defs.append('linearGradient')
      .attr('id', 'tension-gradient')
      .attr('gradientUnits', 'userSpaceOnUse');
    
    gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#22c55e'); // Green
    
    gradient.append('stop')
      .attr('offset', '50%')
      .attr('stop-color', '#eab308'); // Yellow
    
    gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#ef4444'); // Red

    // Create arrow marker
    defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 20)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 8)
      .attr('markerHeight', 8)
      .append('path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5')
      .attr('fill', '#999');

    // Create glow filter for conflict hotspots
    const filter = defs.append('filter')
      .attr('id', 'glow');
    
    filter.append('feGaussianBlur')
      .attr('stdDeviation', '3')
      .attr('result', 'coloredBlur');
    
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Calculate min/max tension for relative coloring
    const tensions = edges.map(e => e.maxTension).filter(t => t > 0);
    const minTension = tensions.length > 0 ? Math.min(...tensions) : 0;
    const maxTension = tensions.length > 0 ? Math.max(...tensions) : 0;

    // Calculate min/max risk for relative coloring
    const riskScores = nodes.map(n => n.riskScore).filter(r => r > 0);
    const minRisk = riskScores.length > 0 ? Math.min(...riskScores) : 0;
    const maxRisk = riskScores.length > 0 ? Math.max(...riskScores) : 0;

    console.log('[FORCE GRAPH] Risk scores:', { riskScores, minRisk, maxRisk });
    console.log('[FORCE GRAPH] Nodes:', nodes.map(n => ({ id: n.id, name: n.name, riskScore: n.riskScore })));

    // Render edges
    const edgeGroup = g.append('g').attr('class', 'edges');
    
    const edgeElements = edgeGroup.selectAll<SVGLineElement, GraphEdge>('.edge')
      .data(edges)
      .enter()
      .append('line')
      .attr('class', 'edge')
      .attr('stroke', d => getRelativeTensionColor(d.maxTension, minTension, maxTension))
      .attr('stroke-width', d => getEdgeThickness(d.financialImpact))
      .attr('stroke-opacity', 0.6)
      .attr('marker-end', 'url(#arrowhead)')
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        setClickedEdge(d.id);
        if (onEdgeClick) onEdgeClick(d.id);
      })
      .on('mouseenter', (event, d) => {
        setHoveredEdge(d.id);
        d3.select(event.currentTarget)
          .attr('stroke-opacity', 1)
          .attr('stroke-width', getEdgeThickness(d.financialImpact) + 2);
      })
      .on('mouseleave', (event, d) => {
        setHoveredEdge(null);
        d3.select(event.currentTarget)
          .attr('stroke-opacity', 0.6)
          .attr('stroke-width', getEdgeThickness(d.financialImpact));
      });

    // Render nodes
    const nodeGroup = g.append('g').attr('class', 'nodes');
    
    const nodeElements = nodeGroup.selectAll<SVGGElement, GraphNode>('.node')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .style('cursor', 'pointer')
      .call(d3.drag<SVGGElement, GraphNode>()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));

    // Add circles for nodes
    nodeElements.append('circle')
      .attr('r', d => getNodeRadius(d.riskScore))
      .attr('fill', d => getNodeColorByRisk(d.riskScore, minRisk, maxRisk))
      .attr('stroke', d => getNodeBorderColor(d))
      .attr('stroke-width', 2)
      .attr('filter', d => d.riskScore > maxRisk * 0.7 ? 'url(#glow)' : 'none') // Glow for top 30% risk
      .on('click', (event, d) => {
        event.stopPropagation();
        if (onNodeClick) onNodeClick(d.id);
      })
      .on('mouseenter', (event, d) => {
        setHoveredNode(d.id);
        if (onNodeHover) onNodeHover(d.id);
        d3.select(event.currentTarget)
          .attr('r', getNodeRadius(d.riskScore) + 5)
          .attr('stroke-width', 3);
      })
      .on('mouseleave', (event, d) => {
        setHoveredNode(null);
        if (onNodeHover) onNodeHover(null);
        d3.select(event.currentTarget)
          .attr('r', getNodeRadius(d.riskScore))
          .attr('stroke-width', 2);
      });

    // Add labels for nodes (moved further away to avoid overlap with data type indicators)
    nodeElements.append('text')
      .text(d => d.name)
      .attr('font-size', '10px')
      .attr('dx', d => getNodeRadius(d.riskScore) + 15)
      .attr('dy', 4)
      .attr('fill', '#333')
      .style('pointer-events', 'none');

    // Add data type indicators (small circles) - only if enabled
    if (showDataTypeIndicators) {
      nodeElements.each(function(d) {
        const node = d3.select(this);
        const radius = getNodeRadius(d.riskScore);
        
        d.dataTypes.slice(0, 3).forEach((dataType, i) => {
          const angle = (i * 120) * (Math.PI / 180);
          const x = Math.cos(angle) * (radius + 8);
          const y = Math.sin(angle) * (radius + 8);
          
          node.append('circle')
            .attr('cx', x)
            .attr('cy', y)
            .attr('r', 4)
            .attr('fill', getDataTypeColor(dataType))
            .attr('stroke', 'white')
            .attr('stroke-width', 1)
            .style('pointer-events', 'none');
        });
      });
    }

    // Update positions on simulation tick
    simulation.on('tick', () => {
      edgeElements
        .attr('x1', d => (d.source as GraphNode).x || 0)
        .attr('y1', d => (d.source as GraphNode).y || 0)
        .attr('x2', d => (d.target as GraphNode).x || 0)
        .attr('y2', d => (d.target as GraphNode).y || 0);
      
      nodeElements
        .attr('transform', d => `translate(${d.x || 0},${d.y || 0})`);
    });

    // Clear selection on background click
    svg.on('click', () => {
      setClickedEdge(null);
      if (onNodeClick) onNodeClick('');
      if (onEdgeClick) onEdgeClick('');
    });

    console.log('Force-directed graph initialized with', nodes.length, 'nodes and', edges.length, 'edges');

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, [data, width, height, showDataTypeIndicators, onNodeClick, onEdgeClick, onNodeHover]);

  return (
    <div className="force-directed-graph" style={{ position: 'relative' }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ border: '1px solid #ccc', background: '#fafafa' }}
      />
      
      {/* Legend */}
      <div className="graph-legend" style={{
        position: 'absolute',
        bottom: 10,
        left: 10,
        background: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '8px',
        fontSize: '12px',
        maxWidth: '200px',
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Legend</div>
        <div style={{ marginBottom: '6px' }}>
          <div style={{ fontWeight: '600', marginBottom: '2px' }}>Edge Colors (Tension):</div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#22c55e' }}>●</span> Low
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#eab308' }}>●</span> Medium
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#ef4444' }}>●</span> High
          </div>
        </div>
        <div style={{ paddingTop: '6px', borderTop: '1px solid #eee' }}>
          <div style={{ fontWeight: '600', marginBottom: '2px' }}>Node Colors (Risk):</div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#3b82f6' }}>●</span> Low Risk
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#eab308' }}>●</span> Medium Risk
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#dc2626' }}>●</span> High Risk
          </div>
        </div>
        <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #eee', fontSize: '11px', color: '#666' }}>
          Edge thickness = Financial Impact
        </div>
      </div>

      {/* Data Type Legend */}
      {showDataTypeIndicators && (
        <div className="data-type-legend" style={{
          position: 'absolute',
          bottom: 10,
          right: 10,
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '8px',
          fontSize: '12px',
          maxWidth: '180px',
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Data Types</div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#3b82f6' }}>●</span> PII
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#f59e0b' }}>●</span> Financial
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#ef4444' }}>●</span> Health
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#10b981' }}>●</span> Payment
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#8b5cf6' }}>●</span> Government
          </div>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>●</span> Other
          </div>
        </div>
      )}

      {/* Tooltip */}
      {(hoveredNode || hoveredEdge || clickedEdge) && (
        <div style={{
          position: 'absolute',
          top: 10,
          right: 10,
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '8px',
          fontSize: '12px',
          maxWidth: '300px',
          pointerEvents: 'none',
        }}>
          {hoveredNode && (
            <div>
              <strong>Node:</strong> {data.nodes.find(n => n.id === hoveredNode)?.name}
            </div>
          )}
          {(hoveredEdge || clickedEdge) && (() => {
            const edge = data.edges.find(e => e.id === (clickedEdge || hoveredEdge));
            return edge ? (
              <div>
                <div style={{ marginBottom: '4px' }}>
                  <strong>Relationship:</strong> {edge.relationshipType}
                </div>
                {edge.description && (
                  <div style={{ 
                    marginTop: '8px', 
                    paddingTop: '8px', 
                    borderTop: '1px solid #e5e7eb',
                    color: '#374151',
                    lineHeight: '1.5'
                  }}>
                    <strong>Description:</strong>
                    <div style={{ marginTop: '4px' }}>{edge.description}</div>
                  </div>
                )}
                {edge.tension && (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', fontSize: '11px' }}>
                    <div>Source → Target: {edge.tension.sourceToTarget.toFixed(2)}</div>
                    <div>Target → Source: {edge.tension.targetToSource.toFixed(2)}</div>
                  </div>
                )}
              </div>
            ) : null;
          })()}
        </div>
      )}
    </div>
  );
};

// Helper functions

/**
 * Map tension to color using relative scale (green -> yellow -> red)
 * Based on min/max tension in the current graph
 */
function getRelativeTensionColor(tension: number, minTension: number, maxTension: number): string {
  // Handle edge cases
  if (maxTension === minTension) return '#eab308'; // Yellow if all same
  if (tension <= 0) return '#22c55e'; // Green for zero/negative
  
  // Normalize tension to 0-1 range
  const normalized = (tension - minTension) / (maxTension - minTension);
  
  // Map to color spectrum: green (0) -> yellow (0.5) -> red (1)
  if (normalized < 0.5) {
    // Green to Yellow
    const t = normalized * 2; // 0 to 1
    return interpolateColor('#22c55e', '#eab308', t);
  } else {
    // Yellow to Red
    const t = (normalized - 0.5) * 2; // 0 to 1
    return interpolateColor('#eab308', '#ef4444', t);
  }
}

/**
 * Interpolate between two hex colors
 */
function interpolateColor(color1: string, color2: string, t: number): string {
  const r1 = parseInt(color1.slice(1, 3), 16);
  const g1 = parseInt(color1.slice(3, 5), 16);
  const b1 = parseInt(color1.slice(5, 7), 16);
  
  const r2 = parseInt(color2.slice(1, 3), 16);
  const g2 = parseInt(color2.slice(3, 5), 16);
  const b2 = parseInt(color2.slice(5, 7), 16);
  
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Map financial impact to edge thickness (1-10 pixels)
 */
function getEdgeThickness(financialImpact: number): number {
  return Math.max(1, Math.min(10, financialImpact / 100000));
}

/**
 * Map risk score to node radius - CONSTANT SIZE
 */
function getNodeRadius(riskScore: number): number {
  return 20; // Constant radius for all nodes
}

/**
 * Map risk score to node color using relative scale (blue -> yellow -> red)
 * Based on min/max risk in the current graph
 */
function getNodeColorByRisk(riskScore: number, minRisk: number, maxRisk: number): string {
  // Handle edge cases
  if (maxRisk === minRisk) return '#eab308'; // Yellow if all same
  if (riskScore <= 0) return '#3b82f6'; // Blue for zero/negative
  
  // Normalize risk to 0-1 range
  const normalized = (riskScore - minRisk) / (maxRisk - minRisk);
  
  // Map to color spectrum: blue (0) -> yellow (0.5) -> red (1)
  if (normalized < 0.5) {
    // Blue to Yellow
    const t = normalized * 2; // 0 to 1
    return interpolateColor('#3b82f6', '#eab308', t);
  } else {
    // Yellow to Red
    const t = (normalized - 0.5) * 2; // 0 to 1
    return interpolateColor('#eab308', '#dc2626', t);
  }
}

/**
 * Determine node color based on data types (DEPRECATED - now using risk-based coloring)
 */
function getNodeColor(node: GraphNode): string {
  // Priority: Health > Financial > PII > Other
  if (node.dataTypes.some(dt => dt.toLowerCase().includes('health'))) {
    return '#ef4444'; // Red for health data
  }
  if (node.dataTypes.some(dt => dt.toLowerCase().includes('financial'))) {
    return '#f59e0b'; // Orange for financial data
  }
  if (node.dataTypes.some(dt => dt.toLowerCase().includes('pii'))) {
    return '#3b82f6'; // Blue for PII
  }
  return '#6b7280'; // Gray for other
}

/**
 * Determine node border color based on regulatory categories
 */
function getNodeBorderColor(node: GraphNode): string {
  if (node.regulatoryCategories.length === 0) return '#d1d5db';
  if (node.regulatoryCategories.length === 1) return '#8b5cf6'; // Purple
  return '#ec4899'; // Pink for multiple categories
}

/**
 * Get color for data type indicator
 */
function getDataTypeColor(dataType: string): string {
  const type = dataType.toLowerCase();
  if (type.includes('pii')) return '#3b82f6';
  if (type.includes('financial')) return '#f59e0b';
  if (type.includes('health')) return '#ef4444';
  if (type.includes('payment')) return '#10b981';
  if (type.includes('government')) return '#8b5cf6';
  return '#6b7280';
}
